import { forwardRef, useCallback, useEffect, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { ArrowRight, Bell, CheckCheck, MessageSquare } from "lucide-react"

import { Avatar, AvatarStack } from "./Avatar"
import { useShellActivity } from "./ShellActivity"
import NotifIcon from "./notifications/NotifIcon"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { csrfToken } from "@/lib/orderActions"
import { cn } from "@/lib/utils"

// The two topbar dropdowns — Messages and Notifications — ported from the design
// handoff's shared PMNotify module. One behavior source, two surfaces: both sit
// on the DS Popover primitive (Radix owns open state + outside-click/Escape
// dismissal) with the same panel chrome (head with a count + "mark all read", a
// scroll body, a footer link into the full route). These are panels, not menus —
// Popover, not DropdownMenu (Hallmark R5): no menu/menuitem ARIA.
//
// Both are REAL. Messages reads the shell's shared activity refresher, so unread
// state arrives on any AppShell page without a visit; Notifications remains lazy
// until its panel opens. Their dots are driven by the same server presenters as
// the full pages, so the dropdowns and destination pages cannot drift.

// The DS panel chrome that dresses PopoverContent for both surfaces. Kept on the
// content element itself; Popover portals and positions it (align/sideOffset).
const PANEL_CLASS =
  "w-[392px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl border-border bg-card p-0 shadow-[0_28px_60px_-22px_rgba(15,18,30,0.42),0_6px_18px_-10px_rgba(15,18,30,0.22)]"

// The topbar icon button that toggles a panel. Mirrors the shell's other topbar
// controls; the unread dot is a real-state signal, not decoration. forwardRef +
// prop passthrough so PopoverTrigger asChild can wire Radix's trigger props/ref
// onto the button; Radix supplies aria-expanded/aria-haspopup=dialog itself.
const TriggerButton = forwardRef(function TriggerButton({ Icon, label, dot, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className="relative grid size-10 place-items-center rounded-[10px] border border-border bg-card text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      {...props}
    >
      <Icon className="size-[17px]" strokeWidth={1.9} />
      {dot && (
        <span className="absolute top-[8px] right-[9px] size-[7px] rounded-full border-[1.5px] border-card bg-primary shadow-[0_0_8px_var(--primary)]" />
      )}
    </button>
  )
})

function PanelHead({ title, count = 0, onMarkAll }) {
  return (
    <div className="flex items-center justify-between border-b border-border px-[15px] py-[13px]">
      <div className="flex items-center gap-2 font-display text-[14.5px] font-semibold text-foreground">
        {title}
        {count > 0 && (
          <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-[5px] text-[11px] font-bold text-primary-foreground">
            {count}
          </span>
        )}
      </div>
      {onMarkAll && (
        <button
          type="button"
          onClick={onMarkAll}
          className="flex items-center gap-[5px] rounded-[7px] px-[6px] py-1 text-[12px] font-semibold text-ink-3 transition-colors hover:bg-secondary hover:text-foreground"
        >
          <CheckCheck className="size-[14px]" strokeWidth={2} />
          Mark all read
        </button>
      )}
    </div>
  )
}

function PanelFoot({ href, label, onClick }) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="group flex items-center justify-center gap-[7px] border-t border-border bg-card px-[13px] py-[13px] text-[13px] font-semibold text-foreground no-underline transition-colors hover:bg-secondary"
    >
      {label}
      <ArrowRight className="size-[15px] transition-transform group-hover:translate-x-[3px]" strokeWidth={2} />
    </Link>
  )
}

function EmptyState({ icon: Icon, title, text }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <span className="grid size-11 place-items-center rounded-full bg-secondary text-muted-foreground">
        <Icon className="size-[18px]" strokeWidth={1.8} />
      </span>
      {title && <p className="text-[13px] font-semibold text-foreground">{title}</p>}
      {text && <p className="text-[12px] text-ink-3">{text}</p>}
    </div>
  )
}

// A recent chat thread in the Messages dropdown. Mirrors the inbox row shape
// (CustomerMessagesPresenter#recent): project rows carry a team avatar stack +
// subtitle, person rows a single avatar + role. Every row opens the full inbox.
function ThreadRow({ thread, onNavigate }) {
  const unread = thread.unread > 0

  return (
    <Link
      href="/messages"
      onClick={onNavigate}
      className={cn(
        "flex items-start gap-[11px] rounded-[11px] p-[10px] no-underline transition-colors hover:bg-secondary",
        unread && "bg-[var(--red-soft)]"
      )}
    >
      <span className="shrink-0">
        {thread.type === "project" ? (
          <AvatarStack people={thread.team ?? []} size={30} />
        ) : (
          <Avatar initials={thread.initials ?? "?"} name={thread.name} size={34} />
        )}
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[13px] font-semibold text-foreground">{thread.name}</span>
          <span className="shrink-0 text-[11px] font-medium text-ink-3">{thread.time}</span>
        </span>
        <span className="truncate text-[11px] text-ink-3">
          {thread.type === "project" ? thread.subtitle : thread.role}
        </span>
        <span className="mt-[3px] line-clamp-2 text-[12px] leading-[1.4] text-muted-foreground">
          {thread.preview?.who && <b className="font-semibold text-foreground">{thread.preview.who}: </b>}
          {thread.preview?.text}
        </span>
      </span>

      {unread && (
        <span className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center self-center rounded-full bg-primary px-[5px] text-[11px] font-bold text-primary-foreground">
          {thread.unread}
        </span>
      )}
    </Link>
  )
}

// The dot, the rows and the count all come from the shell's shared refresher
// (ShellActivity) — the panel no longer owns a timer, and the same fetch feeds
// the rail badge, the inbox and the order Chat tab. Opening the panel still asks
// for fresh truth; the provider's in-flight guard folds that into a concurrent
// poll instead of issuing a duplicate.
export function MessagesMenu() {
  const [open, setOpen] = useState(false)
  const { threads, unread, refresh, clearAllLocally } = useShellActivity()

  const close = () => setOpen(false)

  function onOpenChange(next) {
    setOpen(next)
    if (next) refresh({ showLoading: threads === null })
  }

  // Persist the clear server-side (AQ-002) via one bounded set-based write, then
  // sync the topbar dot + inbox from the server. Clear shared state first so the
  // dot + rows drop immediately everywhere. Mirrors BellMenu.markAll.
  function markAll() {
    clearAllLocally()
    fetch("/messages/mark_all_read", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to mark messages read")
        router.reload({ only: ["messages", "unreadMessages"] })
      })
      .catch(() => {})
      // Whether the write succeeds or fails, reconcile from server truth. On a
      // failure this restores the count the optimistic clear temporarily hid.
      // Forced, so the reconcile can't adopt a poll that was issued while the
      // write was still open — that answer predates the clear and would put
      // every count back until the next tick.
      .finally(() => refresh({ force: true }))
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <TriggerButton Icon={MessageSquare} label="Messages" dot={unread > 0} />
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className={PANEL_CLASS}>
        <PanelHead title="Messages" count={unread} onMarkAll={unread > 0 ? markAll : undefined} />
        <div className="max-h-[min(460px,64vh)] overflow-y-auto p-1.5">
          {threads === null ? (
            <p className="px-3 py-8 text-center text-[12.5px] text-ink-3">Loading…</p>
          ) : threads.length > 0 ? (
            threads.map((thread) => <ThreadRow key={thread.id} thread={thread} onNavigate={close} />)
          ) : (
            <EmptyState icon={MessageSquare} title="No messages yet" text="Your project chats will show up here." />
          )}
        </div>
        <PanelFoot href="/messages" label="Open inbox" onClick={close} />
      </PopoverContent>
    </Popover>
  )
}

// A bell notification in the dropdown. Mirrors the full page's row shape
// (CustomerNotificationsPresenter#row): tone-colored icon chip, title, sub, time.
// href points at a legacy/order surface (not an Inertia page), so it's a hard
// nav via a plain anchor, not an Inertia <Link>.
function NotifRow({ notification, onNavigate }) {
  const { unread } = notification

  return (
    <a
      href={notification.href || "/notifications"}
      onClick={onNavigate}
      className={cn(
        "flex items-start gap-[11px] rounded-[11px] p-[10px] no-underline transition-colors hover:bg-secondary",
        unread && "bg-[var(--red-soft)]"
      )}
    >
      <NotifIcon icon={notification.icon} tone={notification.tone} size={34} />

      <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <span className="text-[13px] leading-[1.4] font-medium text-foreground">{notification.title}</span>
        {notification.sub && <span className="line-clamp-2 text-[12px] leading-[1.4] text-ink-3">{notification.sub}</span>}
        <span className="mt-[2px] text-[11px] font-medium text-ink-3">{notification.time}</span>
      </span>

      {unread && <span className="mt-[6px] size-[7px] shrink-0 rounded-full bg-primary" />}
    </a>
  )
}

export function BellMenu({ unreadCount = 0 }) {
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState(null) // null = not fetched yet
  const [loading, setLoading] = useState(false)
  const [unread, setUnread] = useState(unreadCount)

  // Follow the shared prop across Inertia visits (a new page carries a fresh
  // count), unless the panel has locally cleared it via "mark all read".
  useEffect(() => {
    setUnread(unreadCount)
  }, [unreadCount])

  const fetchNotifications = useCallback(() => {
    if (notifications !== null || loading) return
    setLoading(true)
    fetch("/menu/notifications", { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((res) => (res.ok ? res.json() : { notifications: [] }))
      .then((data) => setNotifications(data.notifications ?? []))
      .catch(() => setNotifications([]))
      .finally(() => setLoading(false))
  }, [notifications, loading])

  const close = () => setOpen(false)

  // Controlled so the lazy first-open fetch fires as the panel opens.
  function onOpenChange(next) {
    setOpen(next)
    if (next) fetchNotifications()
  }

  // Non-destructive server write (sets read_at); clear the local unread state so
  // the dot + row highlights drop immediately, before the redirect re-renders.
  function markAll() {
    router.post(
      "/notifications/mark_all_read",
      {},
      { headers: { "X-CSRF-Token": csrfToken() }, preserveScroll: true, preserveState: true }
    )
    setUnread(0)
    setNotifications((prev) => (prev ? prev.map((notification) => ({ ...notification, unread: false })) : prev))
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <TriggerButton Icon={Bell} label="Notifications" dot={unread > 0} />
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} className={PANEL_CLASS}>
        <PanelHead title="Notifications" count={unread} onMarkAll={unread > 0 ? markAll : undefined} />
        <div className="max-h-[min(460px,64vh)] overflow-y-auto p-1.5">
          {notifications === null ? (
            <p className="px-3 py-8 text-center text-[12.5px] text-ink-3">Loading…</p>
          ) : notifications.length > 0 ? (
            notifications.map((notification) => (
              <NotifRow key={notification.id} notification={notification} onNavigate={close} />
            ))
          ) : (
            <EmptyState icon={Bell} title="You're all caught up" text="New notifications will show up here." />
          )}
        </div>
        <PanelFoot href="/notifications" label="View all notifications" onClick={close} />
      </PopoverContent>
    </Popover>
  )
}
